import React, { FC, useCallback, useEffect, useState } from 'react'
import { Grid, IconButton, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { FormProvider, useForm } from 'react-hook-form'
import QRCode from 'react-qr-code'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { CurrenciesCrypto } from '@sparling/financial-common'
import { Modal } from '../../../../../../components/Dashboard/Modal'
import { InputField } from '../../../../../../components/Dashboard/InputField'
import { icons } from '../../../../../../constants/icons'
import { createUserWalletRequest, openAddNewWalletPopup } from '../../../../../../store/actions'
import {
  addNewWalletPopupCurrencySelector,
  addNewWalletStartTimeSelector,
  generatedAddressSelector,
  isLoadingCheckClientAddressAndGenerateNewAddressSelector,
  isSuccessCheckClientAddressAndGenerateNewAddressSelector,
  userAvailableWalletCurrenciesSelector,
} from '../../../../../../store/selectors'
import { Loader } from '../../../../../../components/Loader'
import { InputSelect } from '../../../../../../components/Dashboard/InputSelect'
import { createEnum } from '../../../../../../utils/createEnum'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: 600,
    minHeight: 100,
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
  bottom: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: theme.spacing(4),
  },
  address: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(3),
    '& :first-child': {
      paddingRight: theme.spacing(2),
    },
  },
  hash: {
    maxWidth: 185,
    overflow: 'hidden',
  },
  iconCopy: {
    padding: 0,
    color: theme.palette.primary.light,
    opacity: 0.6,
    paddingLeft: theme.spacing(1),
    '& :first-child': {
      padding: 0,
      fontSize: 18,
    },
  },
  description: {
    paddingTop: theme.spacing(3),
    fontSize: 14,
    opacity: 0.8,
  },
  error: {
    textAlign: 'center',
  },
}))

interface IAddNewWalletPopup {
  isOpen: boolean
  modalClose?: () => void
  closePrevModal: () => void
}

type Inputs = {
  currency: CurrenciesCrypto
  clientAddress: string
}

const formatTimeLeft = (ms: number) => {
  const ss = Math.floor(ms / 1000)
  const hours = Math.floor(ss / 3600)
    .toString()
    .padStart(2, '0')
  const minutes = Math.floor((ss % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const seconds = (ss % 60).toString().padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}

export const AddNewWalletPopup: FC<IAddNewWalletPopup> = ({ isOpen, modalClose, closePrevModal }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const currency = useSelector(addNewWalletPopupCurrencySelector)
  const addNewWalletStartTime = useSelector(addNewWalletStartTimeSelector)
  const isLoadingCheckClientAddress = useSelector(isLoadingCheckClientAddressAndGenerateNewAddressSelector)
  const isSuccessGenerateAddress = useSelector(isSuccessCheckClientAddressAndGenerateNewAddressSelector)
  const generatedAddress = useSelector(generatedAddressSelector)

  const userAvailableWalletCurrencies = useSelector(userAvailableWalletCurrenciesSelector)

  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    // resolver,
  })
  const { handleSubmit, reset } = formMethods

  const copyClipboard = useCallback(() => {
    if (generatedAddress) {
      navigator.clipboard.writeText(generatedAddress).then()
      toast(`Address copied to clipboard!`, { type: 'success' })
    }
  }, [generatedAddress])

  const onClose = useCallback(() => {
    dispatch(openAddNewWalletPopup({ open: false }))
    reset()
  }, [dispatch, reset])

  const handleClientAddress = useCallback(
    (data: Inputs) => {
      dispatch(createUserWalletRequest({ currency: data.currency, address: data.clientAddress }))
    },
    [dispatch],
  )

  useEffect(() => {
    if (isOpen) closePrevModal()
  }, [isOpen]) // eslint-disable-line

  useEffect(() => {
    if (!addNewWalletStartTime) return undefined

    const interval = setInterval(() => {
      const diff = new Date(addNewWalletStartTime).getTime() + 24 * 60 * 60 * 1000 - Date.now()

      if (diff <= 0) {
        clearInterval(interval)
        setTimeLeft(0)
      } else {
        setTimeLeft(diff)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [addNewWalletStartTime])

  return (
    <Modal
      title={'Add New Wallet'}
      isOpen={isOpen}
      modalClose={modalClose || onClose}
      isSubmit={!isSuccessGenerateAddress}
      onSubmit={handleSubmit(handleClientAddress)}
      submitTitle={'Next'}
      width={650}
      isWidthAuto
      cancelTitle={'Close'}
    >
      {!addNewWalletStartTime && (
        <Grid container>
          <Grid item xs={12}>
            <Typography variant={'inherit'}>Your white listing process starts now!</Typography>
          </Grid>
        </Grid>
      )}
      <FormProvider {...formMethods}>
        <Grid container className={classes.container}>
          {isLoadingCheckClientAddress && <Loader />}
          {!isSuccessGenerateAddress && (
            <Grid item xs={12} display={'flex'} alignItems={'center'} justifyContent={'flex-start'}>
              <Grid item xs={3}>
                <Typography variant={'inherit'}>Add Your Wallet</Typography>
              </Grid>
              <Grid item xs={3}>
                <InputSelect
                  inputName={'currency'}
                  width={120}
                  mb={'0px'}
                  enums={createEnum(userAvailableWalletCurrencies)}
                  defaultValue={currency || userAvailableWalletCurrencies[0] || ''}
                  disable={Boolean(currency) || userAvailableWalletCurrencies.length === 0}
                />
              </Grid>
              <Grid item xs={6}>
                <InputField inputName={'clientAddress'} inputType={'text'} width={'100%'} marginBottom={'0px'} />
              </Grid>
            </Grid>
          )}
          {isSuccessGenerateAddress && generatedAddress && generatedAddress.length > 0 && (
            <Grid item xs={12} display={'flex'} alignItems={'center'} flexDirection={'column'}>
              <Grid item>
                <QRCode value={generatedAddress} size={180} />
              </Grid>

              <Grid item className={classes.address}>
                <Typography>Address:</Typography>
                <Typography className={classes.hash}>
                  {generatedAddress.length > 16 ? `${generatedAddress.slice(0, 16)}...` : generatedAddress}
                </Typography>
                <IconButton className={classes.iconCopy} onClick={copyClipboard}>
                  <span className="material-icons">{icons.COPY}</span>
                </IconButton>
              </Grid>
              <Typography className={classes.description}>
                For wiring instructions please contact your account rep!
              </Typography>
            </Grid>
          )}

          {isSuccessGenerateAddress && (!generatedAddress || generatedAddress.length === 0) && (
            <Grid item xs={12}>
              <Typography className={classes.error}>No founds results</Typography>
            </Grid>
          )}
        </Grid>
      </FormProvider>
      {!!(addNewWalletStartTime && timeLeft) && (
        <Grid container>
          <Grid item xs={12} className={classes.bottom}>
            {formatTimeLeft(timeLeft)}
          </Grid>
        </Grid>
      )}
    </Modal>
  )
}
